import { put, select, call } from 'redux-saga/effects'
import { chargingTokenApiService, handleError } from 'Services/ApiService.js'
import chargingTokenActions from 'Stores/ChargingToken/Actions'
import ChargingTokenSelectors from 'Stores/ChargingToken/Selectors'
import history from 'history/browser'
import {
  encodeObjectToBase64,
  decodeBase64ToObject,
} from '../Utils/Base64Functions'
import i18n from 'i18next'
import { IFToastMessage } from 'Components'
import { toaster } from 'rsuite'

export function* fetchChargingTokens({ offset, shouldShowError }) {
  const query = new URLSearchParams(history.location.search)
  const slashArray = history.location.pathname.split('/')
  let filterParam = query.get('filter')
  let filterObject = []
  let defaultChargingTokenFilters = yield select(
    ChargingTokenSelectors.getChargingTokenFilter,
  )
  try {
    if (filterParam === null) {
      filterObject = defaultChargingTokenFilters
      filterParam = encodeObjectToBase64(filterObject)
    } else {
      try {
        filterObject = decodeBase64ToObject(filterParam)
        for (let i = 0; i < filterObject.length; i++) {
          const { type: type1, title: title1 } = filterObject[i]
          const { type: type2, title: title2 } = defaultChargingTokenFilters[i]

          if (type1 !== type2 || title1 !== title2) {
            filterObject = defaultChargingTokenFilters
            filterParam = encodeObjectToBase64(filterObject)
          }
        }
      } catch (error) {
        filterObject = defaultChargingTokenFilters
        filterParam = encodeObjectToBase64(filterObject)
      }
    }
    if (offset === 0) {
      yield put(chargingTokenActions.fetchChargingTokensLoading())
    }

    const { data } = yield call(chargingTokenApiService.get, '/', {
      params: {
        filter: filterParam,
        offset: offset,
      },
    })
    yield put(
      chargingTokenActions.fetchChargingTokensSuccess(
        offset,
        data.chargingTokens,
        data.nextOffset,
        filterObject,
      ),
    )
  } catch (e) {
    yield put(chargingTokenActions.fetchChargingTokensError(e))
    if (!shouldShowError) handleError(e)
  }
}

export function* editChargingToken({ chargingTokenId, updatedValues }) {
  yield put(chargingTokenActions.editChargingTokenLoading())
  try {
    const { data } = yield call(
      chargingTokenApiService.patch,
      `/${chargingTokenId}`,
      {
        huid: updatedValues.huid,
        contractId: updatedValues.contractId,
        design: updatedValues.design,
        reference: updatedValues.reference,
        parentId: updatedValues.parentId,
        voucherCode: updatedValues.voucherCode,
      },
    )
    yield put(chargingTokenActions.editChargingTokenSuccess(data))
  } catch (e) {
    yield put(chargingTokenActions.editChargingTokenError(e))
    handleError(e)
  }
}

export function* deleteChargingToken({ chargingTokenId }) {
  yield put(chargingTokenActions.deleteChargingTokenLoading())
  try {
    yield call(chargingTokenApiService.delete, `/${chargingTokenId}`)
    yield put(chargingTokenActions.deleteChargingTokenSuccess(chargingTokenId))
    toaster.push(
      <IFToastMessage
        type="success"
        text={i18n.t('ChargingTokenPage.ChargingTokenDeleted')}
      />,
    )
  } catch (e) {
    yield put(chargingTokenActions.deleteChargingTokenError(e))
    handleError(e)
  }
}

export function* createChargingToken({ values }) {
  yield put(chargingTokenActions.createChargingTokenLoading())
  try {
    const { data } = yield call(chargingTokenApiService.post, '/', {
      huid: values.huid,
      contractId: values.contractId,
      design: values.design,
      reference: values.reference,
      voucherCode: values.voucherCode,
    })

    yield put(chargingTokenActions.createChargingTokenSuccess(data))
    toaster.push(
      <IFToastMessage
        type="success"
        text={i18n.t('ChargingTokenPage.ChargingTokenCreated')}
      />,
    )
  } catch (e) {
    yield put(chargingTokenActions.createChargingTokenError(e))
    handleError(e)
  }
}

export function* createChargingTokenBulk({ chargingToken }) {
  yield put(chargingTokenActions.createChargingTokenBulkLoading())
  try {
    const { data } = yield call(
      chargingTokenApiService.post,
      '/chargingTokenBulk',
      {
        chargingTokens: chargingToken.chargingToken,
      },
    )
    yield put(chargingTokenActions.createChargingTokenBulkSuccess(data))
    toaster.push(
      <IFToastMessage
        type="success"
        text={i18n.t('ChargingTokenPage.ChargingTokenBulkCreated')}
      />,
    )
  } catch (e) {
    yield put(chargingTokenActions.createChargingTokenBulkError(e))
    handleError(e)
  }
}

export function* downloadChargingTokenSampleFile() {
  yield put(chargingTokenActions.downloadChargingTokenSampleFileLoading())
  try {
    const response = yield call(
      chargingTokenApiService.get,
      '/downloadSamplefile',
      {
        responseType: 'blob',
      },
    )

    const blob = new Blob([response.data], {
      type: response.headers['content-type'],
    })
    const downloadUrl = window.URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = downloadUrl
    link.download = 'ChargingTokenSampleFile.csv'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)

    yield put(chargingTokenActions.downloadChargingTokenSampleFileSuccess())
  } catch (e) {
    yield put(chargingTokenActions.downloadChargingTokenSampleFileError(e))
    handleError(e)
  }
}
export function* getChargingTokensCount({}) {
  const query = new URLSearchParams(history.location.search)
  let filterParam = query.get('filter')
  let filterObject = []
  yield put(chargingTokenActions.getChargingTokensCountLoading())
  try {
    const chargingTokenFilter = yield select(
      ChargingTokenSelectors.getChargingTokenFilter,
    )
    if (filterParam === null) {
      filterObject = chargingTokenFilter
      filterParam = encodeObjectToBase64(filterObject)
    } else {
      try {
        filterObject = decodeBase64ToObject(filterParam)
        for (let i = 0; i < filterObject.length; i++) {
          const { type: type1, title: title1, data: data1 } = filterObject[i]
          const { type: type2, title: title2 } = chargingTokenFilter[i]

          if (type1 !== type2 || title1 !== title2) {
            filterObject = chargingTokenFilter
            filterParam = encodeObjectToBase64(filterObject)
          }
        }
      } catch (error) {
        filterObject = chargingTokenFilter
        filterParam = encodeObjectToBase64(filterObject)
      }
    }
    const { data } = yield call(chargingTokenApiService.get, '/count', {
      params: {
        filter: filterParam,
      },
    })
    yield put(
      chargingTokenActions.getChargingTokensCountSuccess(
        data.count,
        filterObject,
      ),
    )
  } catch (e) {
    yield put(chargingTokenActions.getChargingTokensCountError(e))
    handleError(e)
  }
}
